export type RecentTradesColumnId =
  | 'openDate'
  | 'symbol'
  | 'side'
  | 'notes'
  | 'volume'
  | 'execs'
  | 'pnl'
  | 'pnlPercentEquities'
  | 'status'
  | 'tags'
  | 'pnlTicksGrossFutures'
  | 'pnlTicksPerContractGrossFutures'
  | 'commission'
  | 'commissionAndFees'
  | 'initialRisk'
  | 'pnlR'
  | 'shared';

export type RecentTradesColumnDefinition = {
  key: RecentTradesColumnId;
  label: string;
  assetClass?: 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO';
};

export const RECENT_TRADES_DEFAULT_ACTIVE_COLUMNS: RecentTradesColumnId[] = [
  'openDate',
  'symbol',
  'side',
  'volume',
  'execs',
  'pnlPercentEquities',
  'pnl',
  'status',
];

export const RECENT_TRADES_COLUMNS: RecentTradesColumnDefinition[] = [
  { key: 'openDate', label: 'Open Date' },
  { key: 'symbol', label: 'Symbol' },
  { key: 'side', label: 'Side' },
  { key: 'notes', label: 'Setup / Notes' },
  { key: 'volume', label: 'Quantity' },
  { key: 'execs', label: 'Fills' },
  { key: 'pnlPercentEquities', label: 'P&L (%)', assetClass: 'STOCKS' },
  { key: 'pnl', label: 'P&L' },
  { key: 'status', label: 'Status' },
  { key: 'tags', label: 'Tags' },
  { key: 'commission', label: 'Commission' },
  { key: 'commissionAndFees', label: 'Commission and Fees' },
  { key: 'initialRisk', label: 'Initial Risk' },
  { key: 'pnlR', label: 'P&L (R)' },
];

export const RECENT_TRADES_DEFAULT_COLUMN_ORDER: RecentTradesColumnId[] = RECENT_TRADES_COLUMNS.map((column) => column.key);

export const RECENT_TRADES_LEGACY_DEFAULT_COLUMN_ORDER: RecentTradesColumnId[] = [
  'status',
  'openDate',
  'symbol',
  'side',
  'volume',
  'execs',
  'tags',
  'notes',
  'pnlPercentEquities',
  'pnl',
  'pnlTicksGrossFutures',
  'pnlTicksPerContractGrossFutures',
  'commission',
  'commissionAndFees',
  'initialRisk',
  'pnlR',
  'shared',
];

export const RECENT_TRADES_COLUMN_IDS = new Set<RecentTradesColumnId>(
  RECENT_TRADES_COLUMNS.map((column) => column.key)
);

export function buildDefaultRecentTradesColumns(): Record<RecentTradesColumnId, boolean> {
  const active = new Set(RECENT_TRADES_DEFAULT_ACTIVE_COLUMNS);
  return RECENT_TRADES_DEFAULT_COLUMN_ORDER.reduce((acc, key) => {
    acc[key] = active.has(key);
    return acc;
  }, {} as Record<RecentTradesColumnId, boolean>);
}

export function sanitizeRecentTradesColumnVisibility(value: unknown): Record<RecentTradesColumnId, boolean> {
  const next = buildDefaultRecentTradesColumns();
  if (!value || typeof value !== 'object' || Array.isArray(value)) return next;

  Object.entries(value as Record<string, unknown>).forEach(([rawKey, rawValue]) => {
    if (!RECENT_TRADES_COLUMN_IDS.has(rawKey as RecentTradesColumnId)) return;
    next[rawKey as RecentTradesColumnId] = Boolean(rawValue);
  });

  // Setup / Notes is no longer a dashboard column. Keep its ID in the config
  // only so older saved preferences can be read without breaking migration.
  next.notes = false;

  const selected = RECENT_TRADES_DEFAULT_COLUMN_ORDER.filter((key) => next[key]);
  if (selected.length === 0) return buildDefaultRecentTradesColumns();

  return next;
}

const arraysEqual = (left: string[], right: string[]) => (
  left.length === right.length && left.every((value, index) => value === right[index])
);

export function sanitizeRecentTradesColumnOrder(value: unknown): RecentTradesColumnId[] {
  if (!Array.isArray(value) || !value.every((key) => typeof key === 'string')) {
    return [...RECENT_TRADES_DEFAULT_COLUMN_ORDER];
  }

  const valid = value.filter((key): key is RecentTradesColumnId => RECENT_TRADES_COLUMN_IDS.has(key as RecentTradesColumnId));
  if (!valid.length || arraysEqual(valid, RECENT_TRADES_LEGACY_DEFAULT_COLUMN_ORDER)) {
    return [...RECENT_TRADES_DEFAULT_COLUMN_ORDER];
  }

  const seen = new Set<RecentTradesColumnId>();
  const deduped = valid.filter((key) => {
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const missing = RECENT_TRADES_DEFAULT_COLUMN_ORDER.filter((key) => !seen.has(key));
  return [...deduped, ...missing];
}
